/**
 * JSON Utilities for Streaming
 *
 * Handles JSON parsing with repair for common AI output issues.
 */

// =============================================================================
// JSON PARSING WITH REPAIR
// =============================================================================

/** Max JSON string length to attempt repair (prevent ReDoS) */
const MAX_REPAIR_LENGTH = 50000;

/**
 * Try to parse JSON with basic repairs for common AI output issues.
 * Handles: unescaped newlines in strings, missing closing brackets.
 */
export function tryParseJSON(jsonStr: string): unknown {
  // First try direct parse (always attempt, even for long strings)
  try {
    return JSON.parse(jsonStr);
  } catch {
    // Try repairs only for reasonable-length strings
  }

  // Skip expensive repairs for very long strings (prevent ReDoS)
  if (jsonStr.length > MAX_REPAIR_LENGTH) {
    console.warn('[JSON] String too long for repair, skipping:', jsonStr.length);
    return null;
  }

  let repaired = jsonStr;

  // Fix unescaped newlines inside strings
  // Match strings and escape any actual newlines within them
  repaired = repaired.replace(/"([^"\\]|\\.)*"/g, (match) => {
    return match
      .replace(/\n/g, '\\n')
      .replace(/\r/g, '\\r')
      .replace(/\t/g, '\\t');
  });

  try {
    return JSON.parse(repaired);
  } catch {
    // Continue to bracket fixing
  }

  // Try to fix missing closing brackets
  // Count open vs close brackets
  let braceCount = 0;
  let bracketCount = 0;

  for (const char of repaired) {
    if (char === '{') braceCount++;
    else if (char === '}') braceCount--;
    else if (char === '[') bracketCount++;
    else if (char === ']') bracketCount--;
  }

  // Add missing closing brackets
  while (bracketCount > 0) {
    repaired += ']';
    bracketCount--;
  }
  while (braceCount > 0) {
    repaired += '}';
    braceCount--;
  }

  // Final attempt
  try {
    return JSON.parse(repaired);
  } catch {
    // Give up - return null to indicate failure
    return null;
  }
}

/**
 * Recursively parse string values that look like JSON within a parsed object.
 *
 * AI models sometimes output nested JSON as string-escaped values:
 *   { "hero": "{\"title\": \"Alice\"}", "actions": "[{\"label\": \"Go\"}]" }
 *
 * This walks the parsed result and attempts to parse any string value
 * that starts with '{' or '[', turning them into real objects/arrays.
 * Max depth of 3 to prevent runaway recursion.
 */
export function deepParseJSON(value: unknown, depth = 0): unknown {
  if (depth > 3) return value;

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if ((trimmed.startsWith('{') && trimmed.endsWith('}')) ||
        (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
      const parsed = tryParseJSON(trimmed);
      if (parsed !== null) {
        return deepParseJSON(parsed, depth + 1);
      }
    }
    return value;
  }

  if (Array.isArray(value)) {
    return value.map(item => deepParseJSON(item, depth + 1));
  }

  if (value !== null && typeof value === 'object') {
    const result: Record<string, unknown> = {};
    for (const [key, val] of Object.entries(value as Record<string, unknown>)) {
      result[key] = deepParseJSON(val, depth + 1);
    }
    return result;
  }

  return value;
}
